import { EmployeeProfile } from "components/EmployeeProfile";
import { LoadingSpinner } from "components/LoadingSpinner";
import React from "react";
import { useAppSelector } from "store/hooks";
import { selectAllRrUsers } from "store/reducers";
import { useFetchOrganisationsQuery } from "store/reducers/organisation";

import { EmployeeEdit } from "./employee_edit";
import { Modal } from "../components/modal";

export function isEmployeeUnregistered(e: Employee) {
  // We don't need to register in Xero if attendance isn't enabled
  return e.xero_tenantId == null && e.attendance_enabled;
}

type Props = {
  employees: Employee[];
};

type OrganisationGroup = {
  id: string;
  name: string;
  employees: Employee[];
};

function groupByOrganisation(employees: Employee[], organisationNames: Map<string, string>): OrganisationGroup[] {
  const groupedEmployees = new Map<string, Employee[]>();

  employees.forEach((employee) => {
    const id = employee.xero_tenantId ?? "";
    groupedEmployees.set(id, [...(groupedEmployees.get(id) ?? []), employee]);
  });

  return [...groupedEmployees.entries()]
    .map(([id, organisationEmployees]) => ({
      id,
      name: id ? (organisationNames.get(id) ?? "Unknown organisation") : "No organisation",
      employees: organisationEmployees,
    }))
    .sort((a, b) => {
      if (!a.id) return 1;
      if (!b.id) return -1;
      return a.name.localeCompare(b.name);
    });
}

function OrganisationGroups(props: {
  employees: Employee[];
  organisationNames: Map<string, string>;
  onEdit: (employeeId: number) => void;
}) {
  const { employees, organisationNames, onEdit } = props;

  return groupByOrganisation(employees, organisationNames).map((group) => (
    <EmployeeTable
      key={group.id}
      title={`${group.name} (${group.employees.length})`}
      employees={group.employees}
      onEdit={onEdit}
    />
  ));
}

export function EmployeeTable(props: { title: string; employees: Employee[]; onEdit: (employeeId: number) => void }) {
  const { title, employees, onEdit } = props;

  return (
    <table className="table mt-4">
      <thead>
        <tr>
          <th colSpan={5} className="table-header-name">
            {title}
          </th>
        </tr>
        <tr>
          <th className="table-header-column-name">Employee</th>
          <th className="table-header-column-name">RadReport</th>
          <th className="table-header-column-name">Employment</th>
          <th className="table-header-column-name">Role</th>
          <th className="table-header-column-name">Action</th>
        </tr>
      </thead>
      <tbody>
        {employees.map((employee) => (
          <EmployeeEdit key={employee.id} employee={employee} onEdit={onEdit} />
        ))}
      </tbody>
    </table>
  );
}

export function EmployeesList(props: Props) {
  const { employees } = props;
  const [showTerminated, setShowTerminated] = React.useState(false);
  const [showInactive, setShowInactive] = React.useState(false);
  const [showUnlinked, setShowUnlinked] = React.useState(false);
  const [editingEmployeeId, setEditingEmployeeId] = React.useState<number>();
  const rrUsers = useAppSelector(selectAllRrUsers);
  const rrUsersStatus = useAppSelector((state) => state.rrUser.status);
  const { data: organisationStore } = useFetchOrganisationsQuery();

  if ((rrUsersStatus === "idle" || rrUsersStatus === "loading") && !rrUsers.length) {
    return <LoadingSpinner />;
  }

  const inactiveUserIds = new Set(rrUsers.filter((user) => !user.active).map((user) => user.id));
  const organisationNames = new Map(
    organisationStore?.organisations.map((organisation) => [organisation.xero_tenantId, organisation.xero_tenantName]),
  );
  const isInactive = (employee: Employee) =>
    employee.radreport_user_id != null && inactiveUserIds.has(employee.radreport_user_id);
  const currentAndUnregisteredEmployees = employees.filter((employee) => !employee.xero_TerminationDate);
  const inactiveCount = currentAndUnregisteredEmployees.filter(isInactive).length;
  const unlinkedCount = currentAndUnregisteredEmployees.filter((employee) => employee.radreport_user_id == null).length;

  const visibleEmployees = currentAndUnregisteredEmployees.filter((employee) => {
    if (!showUnlinked && employee.radreport_user_id == null) return false;
    return showInactive || rrUsersStatus !== "succeeded" || !isInactive(employee);
  });

  const notLinkedToXero = visibleEmployees.filter(isEmployeeUnregistered);

  const currentEmployees = visibleEmployees.filter((e) => {
    return !isEmployeeUnregistered(e);
  });

  const terminatedEmployees = employees.filter((e) => {
    return e.xero_TerminationDate;
  });

  return (
    <>
      {!!terminatedEmployees.length && (
        <details onToggle={(event) => setShowTerminated(event.currentTarget.open)}>
          <summary>
            <h1 className="inline">Terminated Employees ({terminatedEmployees.length})</h1>
          </summary>
          {showTerminated && (
            <OrganisationGroups
              employees={terminatedEmployees}
              organisationNames={organisationNames}
              onEdit={setEditingEmployeeId}
            />
          )}
        </details>
      )}
      <h1>Current Employees</h1>
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={showInactive} onChange={(event) => setShowInactive(event.target.checked)} />
          Show inactive RadReport accounts
          {rrUsersStatus === "succeeded" && <span className="text-gray-500">({inactiveCount})</span>}
        </label>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={showUnlinked} onChange={(event) => setShowUnlinked(event.target.checked)} />
          Show employees not linked to RadReport
          <span className="text-gray-500">({unlinkedCount})</span>
        </label>
      </div>
      {rrUsersStatus === "failed" && (
        <p role="alert">Could not load RadReport accounts. Inactive accounts have not been filtered out.</p>
      )}
      {!visibleEmployees.length && <p>No current employees match these filters.</p>}
      {!!notLinkedToXero.length && (
        <EmployeeTable
          title={`Unregistered (${notLinkedToXero.length})`}
          employees={notLinkedToXero}
          onEdit={setEditingEmployeeId}
        />
      )}
      {!!currentEmployees.length && (
        <section>
          <OrganisationGroups
            employees={currentEmployees}
            organisationNames={organisationNames}
            onEdit={setEditingEmployeeId}
          />
        </section>
      )}
      {editingEmployeeId != null && (
        <Modal isOpen dismissModal={() => setEditingEmployeeId(undefined)} title="Edit Employee">
          <EmployeeProfile employeeId={editingEmployeeId} onCancel={() => setEditingEmployeeId(undefined)} />
        </Modal>
      )}
    </>
  );
}
